import { useState } from 'react';
import { ArrowRight, CalendarCheck, ClipboardList, Sparkles, Users } from 'lucide-react';

interface OnboardingWelcomeModalProps {
  open: boolean;
  onStart: () => void | Promise<void>;
}

export function OnboardingWelcomeModal({ open, onStart }: OnboardingWelcomeModalProps) {
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleStart = async () => {
    setLoading(true);
    try {
      await onStart();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl">

        {/* Header con degradado */}
        <div className="relative overflow-hidden px-8 pt-10 pb-8" style={{ backgroundColor: '#212b36' }}>
          {/* Blobs decorativos */}
          <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-white/10" />
          <div className="absolute bottom-0 -left-6 h-24 w-24 rounded-full bg-white/10" />
          <div className="absolute top-6 left-1/2 h-12 w-12 -translate-x-1/2 rounded-full bg-white/10" />

          <div className="relative flex flex-col items-center text-center">
            <img
              src="/ISO_TIPOMESTLY.png"
              alt="Mestly"
              className="mb-4 h-14 w-14 object-contain drop-shadow-xl"
            />
            <h2 className="text-2xl font-black leading-tight tracking-tight text-white">
              ¡Bienvenido a Mestly! 👋
            </h2>
            <p className="mt-2 text-sm font-medium text-white/80 leading-relaxed max-w-sm">
              Tu plataforma para gestionar citas, clientes y el negocio desde un solo lugar.
            </p>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="px-8 py-7">
          <p className="text-center text-slate-600 text-sm leading-relaxed">
            Para empezar, te guiaremos por{' '}
            <span className="font-semibold text-slate-800">5 pasos rápidos</span>{' '}
            que dejarán Mestly listo para operar tu negocio desde el primer día.
          </p>

          {/* Pasos resumidos */}
          <div className="mt-5 space-y-2.5">
            {[
              { icon: <Sparkles className="h-3.5 w-3.5 text-[#2db1c3]" />, text: 'Configura los datos y horarios de tu negocio' },
              { icon: <ClipboardList className="h-3.5 w-3.5 text-[#2db1c3]" />, text: 'Agrega tus categorías y servicios' },
              { icon: <Users className="h-3.5 w-3.5 text-[#2db1c3]" />, text: 'Crea tu equipo de profesionales' },
              { icon: <Users className="h-3.5 w-3.5 text-[#2db1c3]" />, text: 'Registra tu primer cliente' },
              { icon: <CalendarCheck className="h-3.5 w-3.5 text-[#2db1c3]" />, text: 'Agenda tu primera cita' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2.5">
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#e8f7f9]">
                  {item.icon}
                </div>
                <span className="text-xs text-slate-700">{item.text}</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleStart}
            disabled={loading}
            className="mt-6 w-full rounded-2xl bg-gradient-to-r from-[#0f7a8a] to-[#58BDB3] py-4 text-sm font-black tracking-wide text-white shadow-lg hover:shadow-xl hover:opacity-95 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span className="flex items-center justify-center gap-2">
              {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {loading ? 'Cargando...' : 'Empezar configuración'}
            </span>
          </button>

          <p className="mt-3 text-center text-[11px] text-slate-400">
            Solo toma unos minutos · Puedes ajustar todo después
          </p>
        </div>
      </div>
    </div>
  );
}
